<script setup lang="ts">
import InputLabel from '@/components/input-label.vue'
import InputSelect from '@/components/input-select.vue'
import ModalContent from '@/components/modal/modal-content.vue'
import ModalFooter from '@/components/modal/modal-footer.vue'
import { useQuery } from '@tanstack/vue-query'
import { fetchOwnersQueryOptions } from '@/shared/queries'
import { ref, toRefs } from 'vue'
import { faPlus, faTimes } from '@fortawesome/pro-regular-svg-icons'
import ButtonPrimary from '@/components/button-primary.vue'
import ButtonSecondary from '@/components/button-secondary.vue'
import ModalTitle from '@/components/modal/modal-title.vue'
import { fetchProductsQueryOptions } from '@/features/projects/queries'
import InputNumber from '@/components/input-number.vue'
import InputText from '@/components/input-text.vue'
import { useForm } from 'vee-validate'
import { toTypedSchema } from '@vee-validate/zod'
import type { checkAssetCreationEligibility } from '@/features/assets/api/checkAssetCreationEligibility'
import { useCreateAsset } from '@/features/assets/mutations'
import { createAssetSchema } from '@/features/assets/schemas'
import { AssetEscalationType } from '@/features/assets/constants'
import useNotifications from '@/composables/notifications'

type LookupData = Awaited<ReturnType<typeof checkAssetCreationEligibility>>

const emit = defineEmits<{ close: [] }>()
const props = defineProps<{ details: LookupData }>()
const { details } = toRefs(props)

const selectedOwner = ref<number>()
const selectedProduct = ref<number>()
const selectedEscalation = ref<'fixed' | 'cpi' | 'utility'>()
const { data: owners } = useQuery(fetchOwnersQueryOptions())
const { data: products } = useQuery(fetchProductsQueryOptions())
const { mutateAsync: createAssetAsync, isPending: isCreatingAsset } = useCreateAsset()
const { addSuccess, addError } = useNotifications()

const { handleSubmit } = useForm({
  validationSchema: toTypedSchema(createAssetSchema),
  initialValues: {
    unifiiProjectCode: details.value.unifiiProjectCode,
    type: details.value.type
  }
})
const saveAsset = handleSubmit(
  values => createAssetAsync(values).then(
    () => {
      addSuccess({
        title: 'New asset created successfully.',
        message: 'You are now able to continue setting up this asset.'
      })
      emit('close')
    },
    () => {
      addError({
        title: 'Unable to create new asset.',
        message: 'Please try again. If the problem persists, please contact support.'
      })
    }
  )
)

</script>

<template>
  <ModalTitle>Adding New Asset</ModalTitle>
  <ModalContent>
    <div class="d-grid align-items-center" style="grid-template-columns: 126px 186px auto; gap: 13px">
      <input type="hidden" name="type" :value="details.type" />

      <InputLabel>Project Code</InputLabel>
      <span :class="$style.span">{{ details.unifiiProjectCode }}</span>

      <InputLabel>Project Name</InputLabel>
      <span :class="$style.span">{{ details.projectName }}</span>

      <InputLabel>Project Address</InputLabel>
      <span :class="$style.span">{{ details.projectAddress }}</span>

      <InputLabel>Product</InputLabel>
      <InputSelect :class="$style.span"
                   :items="(products ?? [ ]).map(({ id, name }) => ({ text: name, value: id }))"
                   item-text="text"
                   item-value="value"
                   name="productId"
                   error-variant="tooltip"
                   v-model:selected="selectedProduct" />

      <InputLabel>Owner</InputLabel>
      <InputSelect :class="$style.span"
                   :items="(owners ?? []).map(({ id, name }) => ({ text: name, value: id }))"
                   item-text="text"
                   item-value="value"
                   name="ownerId"
                   error-variant="tooltip"
                   v-model:selected="selectedOwner" />

      <InputLabel>System Size</InputLabel>
      <InputNumber required name="systemSize" error-variant="tooltip" />
      <span class="text-muted">kWh</span>

      <InputLabel>Consumption Yield</InputLabel>
      <InputNumber required name="consumptionYield" error-variant="tooltip" />
      <span class="text-muted">kWh</span>

      <InputLabel>Annual Escalation</InputLabel>
      <InputSelect :items="[
                     { text: 'Fixed', value: AssetEscalationType.Fixed },
                     { text: 'CPI Linked', value: AssetEscalationType.CpiLinked },
                     { text: 'Utility Linked', value: AssetEscalationType.UtilityLinked }
                   ]"
                   name="escalationType"
                   item-text="text"
                   item-value="value"
                   error-variant="tooltip"
                   v-model:selected="selectedEscalation" />
      <InputNumber required name="escalationPercentage" error-variant="tooltip">
        <template #suffix>%</template>
      </InputNumber>

      <InputLabel>VCOM System Key</InputLabel>
      <InputText required maxlength="6" :class="$style.span" name="vcomSystemKey" error-variant="tooltip" />
    </div>
  </ModalContent>
  <ModalFooter class="text-end">
    <ButtonSecondary :disabled="isCreatingAsset" :icon="faTimes" @click="$emit('close')">Cancel</ButtonSecondary>
    <ButtonPrimary class="ms-2" :state="isCreatingAsset ? 'pending' : undefined" :icon="faPlus" @click="saveAsset">Create</ButtonPrimary>
  </ModalFooter>
</template>

<style module lang="scss">
.span {
  grid-column: span 2;
}
</style>
